import {
  Box,
  ProportionBar,
  Skeleton,
  Text,
  WidgetDisclosureRow,
  WidgetDisclosureRowSkeleton,
  type Color,
  type ProportionBarSegment,
  type WidgetListColumn,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import { type OutcomePerformanceUserRow } from "./use-outcome-performance";
import {
  FIRST_PAINT_MEMBER_ROWS,
  MEMBER_BAR_PX,
  MEMBER_COUNT_PX,
  MEMBER_NAME_COMPACT_PX,
  MEMBER_NAME_PX,
  MEMBER_RATE_PX,
  RATE_SAMPLE_FLOOR,
  TONE_COLORS,
  TONE_LABELS,
  TONE_ORDER,
  type ToneKey,
} from "./vocabulary";

/** The list's two figure columns: the Updates count, then the rate of the
 *  tone the card is focused on — the header names whichever it is. */
export function memberColumns(rateTone: ToneKey): WidgetListColumn[] {
  return [
    { key: "updates", label: "Updates", width: MEMBER_COUNT_PX },
    { key: "rate", label: TONE_LABELS[rateTone], width: MEMBER_RATE_PX },
  ];
}

/** A member's rate for one tone: of the Updates with an outcome, or — for
 *  the missing bucket — of every Update the member posted. */
function MemberRate({ row, tone }: { row: OutcomePerformanceUserRow; tone: ToneKey }) {
  const count = row[tone];
  const base = tone === "missing" ? row.total : row.completed;
  if (base === 0) {
    return (
      <Text size="sm" variant="faint" tabular>
        {"—"}
      </Text>
    );
  }
  if (base < RATE_SAMPLE_FLOOR) {
    return (
      <Box direction="row" align="baseline" gap="xs">
        <Text size="sm" weight="semibold" tabular>
          {count.toLocaleString()}
        </Text>
        <Text size="xs" variant="muted" tabular>
          of {base.toLocaleString()}
        </Text>
      </Box>
    );
  }
  return (
    <Text size="sm" weight="semibold" tabular>
      {formatShare(count, base)}
    </Text>
  );
}

function memberSegments(row: OutcomePerformanceUserRow): {
  segments: ProportionBarSegment[];
  colors: Color[];
} {
  const segments: ProportionBarSegment[] = [];
  const colors: Color[] = [];
  for (const tone of TONE_ORDER) {
    if (row[tone] === 0) continue;
    segments.push({ id: tone, label: TONE_LABELS[tone], value: row[tone] });
    colors.push(TONE_COLORS[tone]);
  }
  return { segments, colors };
}

function MemberBarRow({
  row,
  columns,
  compact,
  maxTotal,
  focus,
  rateTone,
  selected,
  onClick,
}: {
  row: OutcomePerformanceUserRow;
  columns: readonly WidgetListColumn[];
  compact: boolean;
  maxTotal: number;
  focus: ToneKey | null;
  rateTone: ToneKey;
  selected: boolean;
  onClick: () => void;
}) {
  const { segments, colors } = memberSegments(row);
  const [countColumn, rateColumn] = columns;
  return (
    <WidgetDisclosureRow expanded={selected} label={`${row.label} Project Updates`} onToggle={onClick}>
      <Box direction="row" align="center" gap="md" fill="flex" shrink minWidth={0}>
        {/* Wide, the name column holds its width so the bar starts on the
            tone bar's edge; compact, it yields to keep the bar's room. */}
        <Box width={compact ? MEMBER_NAME_COMPACT_PX : MEMBER_NAME_PX} shrink={compact} minWidth={0}>
          <EntityTag entity={row.entity} display="full" interactive={false} weight="medium" />
        </Box>
        <Box fill="flex" minWidth={0}>
          <ProportionBar
            segments={segments}
            colorTokens={colors}
            thickness={MEMBER_BAR_PX}
            span={maxTotal > 0 ? row.total / maxTotal : 0}
            focusedId={focus}
          />
        </Box>
      </Box>
      <Box width={countColumn?.width} shrink={false} align="end">
        <Text size="sm" weight="medium" variant="muted" tabular>
          {row.total.toLocaleString()}
        </Text>
      </Box>
      <Box width={rateColumn?.width} shrink={false} align="end">
        <MemberRate row={row} tone={rateTone} />
      </Box>
    </WidgetDisclosureRow>
  );
}

export function MemberBars({
  rows,
  columns,
  compact,
  maxTotal,
  focus,
  rateTone,
  onMemberClick,
  selectedActorId,
}: {
  rows: OutcomePerformanceUserRow[];
  columns: readonly WidgetListColumn[];
  compact: boolean;
  maxTotal: number;
  focus: ToneKey | null;
  rateTone: ToneKey;
  onMemberClick: (row: OutcomePerformanceUserRow) => void;
  selectedActorId?: string | undefined;
}) {
  return (
    <Box direction="column">
      {rows.map((row) => (
        <MemberBarRow
          key={row.userId}
          row={row}
          columns={columns}
          compact={compact}
          maxTotal={maxTotal}
          focus={focus}
          rateTone={rateTone}
          selected={selectedActorId === row.userId}
          onClick={() => onMemberClick(row)}
        />
      ))}
    </Box>
  );
}

/** The rows while their figures are in flight — one per member the last
 *  answer ranked (three before any answer), at the row's own geometry. */
export function MemberBarsSkeleton({
  rows,
  columns,
  compact,
  selectedActorId,
}: {
  rows: OutcomePerformanceUserRow[];
  columns: readonly WidgetListColumn[];
  compact: boolean;
  selectedActorId?: string | undefined;
}) {
  const keys =
    rows.length > 0
      ? rows.map((row) => row.userId)
      : Array.from({ length: FIRST_PAINT_MEMBER_ROWS }, (_, index) => `slot-${index}`);
  return (
    <Box direction="column">
      {keys.map((key) => (
        <WidgetDisclosureRowSkeleton key={key} selected={key === selectedActorId}>
          <Box direction="row" align="center" gap="md" fill="flex" shrink minWidth={0}>
            <Box
              width={compact ? MEMBER_NAME_COMPACT_PX : MEMBER_NAME_PX}
              shrink={compact}
              minWidth={0}
              direction="row"
              align="center"
              gap="xs"
            >
              <Skeleton delayed circle width={20} />
              <Skeleton delayed height={10} width="60%" />
            </Box>
            <Box fill="flex" minWidth={0}>
              <Skeleton delayed height={MEMBER_BAR_PX} width="50%" />
            </Box>
          </Box>
          {columns.map((column) => (
            <Box key={column.key} width={column.width} shrink={false} align="end">
              <Skeleton delayed height={10} width={24} />
            </Box>
          ))}
        </WidgetDisclosureRowSkeleton>
      ))}
    </Box>
  );
}
